import mixpanel from "mixpanel-browser"

// THE CLIENT HALF: sessions, page views, device and referrer.
//
// Deliberately NOT the event catalogue. Everything in
// Thrive::EventCatalogue is sent from the server, where it cannot be
// blocked and cannot disagree with what was actually written — a click
// handler fires on a request that then fails validation, and you get a
// funnel counting meals nobody saved.
//
// What the browser knows and the server cannot: how long a visit lasted,
// what the device was, where they came from, and which pages were seen
// without anything being submitted. That is what this is for, and the two
// halves do not overlap.
//
// WHICH MEANS THE ONE THING THAT MUST BE RIGHT IS THE JOIN. The server
// sends distinct_id as String(user.id); this calls identify with the same
// string. Get that wrong and one person becomes two profiles, each
// holding half a funnel — which looks like working analytics until you
// try to follow somebody through it.

const token = () =>
  document.querySelector('meta[name="mixpanel-token"]')?.content

const userId = () =>
  document.querySelector('meta[name="mixpanel-user-id"]')?.content

let started = false

function start() {
  const key = token()
  if (!key || started) return false

  mixpanel.init(key, {
    // No automatic page views: Turbo navigations are not page loads, so
    // the SDK's own tracking would miss almost every one. trackPageView
    // below runs on turbo:load instead, which is the real navigation.
    track_pageview: false,
    // COOKIE, NOT localStorage, so the SERVER can read the anonymous id.
    //
    // This is what lets a pre-signup event be attributed. access_requested
    // fires from Rails before any account exists, so without the browser's
    // device id it goes out with no identity at all and can never be
    // stitched to the person who later signs up — the top of the funnel
    // simply detaches. localStorage is invisible to Rails; a cookie is not.
    persistence: "cookie",
    // Readable by Rails. The SDK's own cookie holds no secret — it is a
    // random device id — and the server only ever reads it.
    cookie_domain: window.location.hostname,
    // OFF, and the reason is the consent banner. ip: false stops Mixpanel
    // deriving a location from the request IP; with it on, we would be
    // collecting location data nobody agreed to. Revisit alongside the
    // banner, not before it.
    ip: false,
    // Session replay, sampled. 100% is affordable at beta scale and is
    // the whole reason for adding a client script — watching somebody
    // hesitate answers "why did they stop" in a way no funnel does.
    record_sessions_percent: 100
  })

  started = true
  return true
}

// IDENTIFY ON EVERY NAVIGATION, not once at boot.
//
// Turbo keeps one JS context across navigations, so a boot-time identify
// would never see somebody signing in — they would stay anonymous until a
// full reload. Re-asserting it per page is cheap (the SDK no-ops when the
// id is unchanged) and it is what makes the sign-in boundary work.
let identified = null

function syncIdentity() {
  const id = userId()

  if (id && id !== identified) {
    mixpanel.identify(id)
    identified = id
    return
  }

  // SIGNED OUT, on a device that had somebody signed in. Without reset,
  // the next person's events land on the previous person's profile —
  // which on a shared or demo device quietly corrupts both.
  if (!id && identified) {
    mixpanel.reset()
    identified = null
  }
}

function trackPageView() {
  mixpanel.track_pageview({ path: window.location.pathname })
}

function onLoad() {
  if (!start() && !started) return

  syncIdentity()
  trackPageView()
}

// turbo:load covers the first load and every Turbo navigation after it.
document.addEventListener("turbo:load", onLoad)
